import { useInfiniteScroll } from '@/hooks/useInfiniteScroll';
import { useTranslation } from '@/i18n';
import {
  ButtonV2,
  IconCollapsed,
  IconExpanded,
  Label,
  Loading,
  Tag,
} from '@moneyforward/ap-frontend-components';

import classnames from 'classnames/bind';
import React, { useMemo } from 'react';
import { usePayeeListHandler } from './hooks';
import styles from './index.module.scss';

const cx = classnames.bind(styles);

interface PayeeInfo {
  name?: string;
  counterparty_name?: string;
  id?: string;
}

interface ListPayeeProps {
  payees: PayeeInfo[];
  isFetchingNextPage: boolean;
  loadMorePayeeList: () => void;
  totalPayees: number;
  label?: string;
}

export const ListPayee: React.FC<ListPayeeProps> = ({
  payees,
  loadMorePayeeList,
  isFetchingNextPage,
  totalPayees,
  label,
}) => {
  const {
    isExpanded,
    hasMultipleRows,
    visibleIndices,
    containerRef,
    toggleExpand,
  } = usePayeeListHandler(payees);

  const { setTargetElement } = useInfiniteScroll(loadMorePayeeList, {
    isLoading: isFetchingNextPage,
  });

  const { t } = useTranslation();

  const remainingPayees = useMemo(
    () => totalPayees - visibleIndices.length,
    [totalPayees, visibleIndices.length]
  );

  return (
    <div className={cx('container')} data-testid='payee-list-container'>
      <div className={cx('header')}>
        <div className={cx('label')}>
          <Label labelContentClassName={cx('label')}>
            {label ?? t('export_instruction_pdf_contact')}
          </Label>
        </div>
        <div className={cx('content')}>
          <div
            ref={containerRef}
            className={cx('tags-list', { expanded: isExpanded })}
            data-testid='payee-tags-list'
            data-expanded={isExpanded}
          >
            {payees.map((payee, index) => (
              <Tag
                key={index}
                closable={false}
                outline={false}
                className={cx('tag', {
                  hidden: !visibleIndices.includes(index),
                })}
                data-testid={`payee-tag-${index}`}
                data-visible={visibleIndices.includes(index)}
              >
                <span className={cx('tag-content')}>
                  <span className={cx('tag-content-name')}>
                    {payee.counterparty_name}
                  </span>
                  <span>/</span>
                  <span className={cx('tag-content-name')}>{payee.name}</span>
                </span>
              </Tag>
            ))}
            {isExpanded && (
              <div ref={setTargetElement} className={cx('loading-container')}>
                {isFetchingNextPage && (
                  <Loading size='medium' color='default' />
                )}
              </div>
            )}
          </div>
          {hasMultipleRows && (
            <div className={cx('actions')}>
              {!isExpanded && (
                <Tag closable={false} outline={false}>
                  {t('export_instruction_pdf_contact_number', {
                    numbers: remainingPayees,
                  })}
                </Tag>
              )}
              <ButtonV2
                size='sm'
                isSecondary
                isTransparent
                rightIcon={isExpanded ? <IconExpanded /> : <IconCollapsed />}
                onClick={toggleExpand}
                data-testid='expand-button'
              >
                {t('export_instruction_pdf_contact_detail')}
              </ButtonV2>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
